import { useEffect, useMemo } from 'react';
import { LocalStorage, KEYS } from '@/utils/LocalStorage';
import { useGetInitData } from '@viome/viome-base/src/usecase/Bootup/BootupQuery';
import type {
  GetInitData,
  IKitType,
  kitDetails,
} from '@viome/viome-base/src/usecase/Bootup/BootupModel';
import { useContextStore, useActiveOrgEntry } from '@/hooks/context/useContextStore';
import {
  buildBootupStatus,
  buildPatientGetInit,
} from '@/hooks/context/bootupNormalize';

export interface BootupStatus {
  supplementRecsExists: boolean;
  recommendationIsLockedByStudyExists: boolean;
  recommendationExists: boolean;
  hasPersonalizedSupplementRecommendationsExists: boolean;
  kitType: IKitType;
  isEligibleForSupplements: boolean;
  rnrLastGeneratedDate: string;
  kitDetailsData: kitDetails;
  CDkitDetailsData: kitDetails;
  preRnRKitType: IKitType;
  isATSUser: boolean;
  /** From getInit; null/undefined means use local COMPONENTIZATION_ELIGIBLE_SKUS. */
  componentizationEligibleSkus?: string[] | null;
}

/**
 * Context-aware bootup (DE-336). Builds `BootupStatus` from the ACTIVE
 * context's payload — patient → the selected org's content entry (+ account
 * extras from the stored getInit v2); consumer → getInit v2 — falling back to
 * **getInit v1** when the context payload is unavailable (e.g. a deep-link that
 * bypassed the post-login resolver). Writes `localStorage['bootup']` so every
 * downstream consumer keeps working unchanged.
 *
 * Note: the v1 `getInit` query/model in ViomeBase is untouched (mobile shares
 * it); all context composition lives here in xfe-v2.
 */
export const useBootup = () => {
  const {
    data: v1Data,
    isError: v1Error,
    isLoading: v1Loading,
  } = useGetInitData();
  const patientOrg = useContextStore((s) => s.patientOrg);
  const consumerInit = useContextStore((s) => s.consumerInit);
  const patientContent = useContextStore((s) => s.patientContent);
  const activeOrgEntry = useActiveOrgEntry();

  // Active context's payload; null until a context is selected/hydrated.
  const contextData = useMemo<GetInitData | null>(() => {
    if (patientOrg && activeOrgEntry && patientContent) {
      return buildPatientGetInit(activeOrgEntry, patientContent, consumerInit);
    }
    if (!patientOrg && consumerInit) return consumerInit;
    return null;
  }, [patientOrg, activeOrgEntry, patientContent, consumerInit]);

  // Prefer the context payload; fall back to v1 getInit.
  const source = contextData ?? v1Data ?? null;

  // Derive synchronously (buildBootupStatus is pure) so bootupState is never
  // transiently null while `source` exists. useState + useEffect would leave a
  // one-render gap on every consumer's mount (useBootup state is per-instance),
  // flickering the home screen null → populated. useMemo closes that gap.
  const bootupState = useMemo<BootupStatus | null>(
    () => (source ? buildBootupStatus(source) : null),
    [source],
  );

  // Effect only persists the snapshot now — it no longer gates render.
  useEffect(() => {
    if (bootupState) {
      LocalStorage.setItem(KEYS.BOOTUP, JSON.stringify(bootupState));
    }
  }, [bootupState]);

  // With a context payload we're ready as soon as bootupState is derived (same
  // render, thanks to useMemo). Otherwise mirror v1's loading state.
  const isLoading = contextData ? !bootupState : v1Loading;
  const isError = contextData ? false : v1Error;

  return { bootupState, isError, isLoading };
};
